import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import ProfileImage from "common/components/ui/ProfileImage";
import SelectButton from "common/components/ui/SelectButton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLinkedInGroups from "../../../datahooks/useLinkedInGroups";

import type { LinkedInGroup } from "../../../types";

import GroupDefaultImage from "assets/images/group-fallback.svg";

interface GroupFormFieldProps {
  accountId: string;
  selectedGroup: LinkedInGroup | undefined;
  onSelect: (group: LinkedInGroup) => void;
}

export default function GroupFormField({
  accountId,
  selectedGroup,
  onSelect,
}: GroupFormFieldProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { linkedInGroups, isLoadingLinkedInGroups } =
    useLinkedInGroups(accountId);

  const [inputValue, setInputValue] = useState("");

  let selectButtonLabel = "Select a group for your targeting";
  if (selectedGroup) {
    selectButtonLabel = selectedGroup.name;
  } else if (isLoadingLinkedInGroups) {
    selectButtonLabel = "Loading groups...";
  }

  const filteredGroups = linkedInGroups
    ? linkedInGroups.filter((group) =>
        group.name.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : [];

  return (
    <FormField
      label="Select a group"
      htmlFor=""
      hint="Select a LinkedIn group to collect its members as leads"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isLoadingLinkedInGroups}>
          <SelectButton
            size={isDesktop ? "md" : "lg"}
            hasSelection={!!selectedGroup}
            isLoading={isLoadingLinkedInGroups}
          >
            {selectButtonLabel}
          </SelectButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
          {linkedInGroups?.length > 4 && (
            <MenuSearchInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}
          <DropdownMenuList>
            {linkedInGroups?.length === 0 ? (
              <p className="p-4 text-center text-black-500">
                You are not a member of any LinkedIn groups
              </p>
            ) : (
              filteredGroups.map((group) => {
                const { id, name, images, public: isPublic } = group;
                const imageUrl = images.length
                  ? images[0].url
                  : GroupDefaultImage;

                return (
                  <DropdownMenuItem key={id} onSelect={() => onSelect(group)}>
                    <ProfileImage src={imageUrl} />
                    <div className="flex flex-col">
                      <h5 className="line-clamp-1 text-left text-button-sm">
                        {name}
                      </h5>
                      <p className="line-clamp-1 text-left text-xs text-black-500">
                        {isPublic ? "Public group" : "Private group"}
                      </p>
                    </div>
                  </DropdownMenuItem>
                );
              })
            )}
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>
    </FormField>
  );
}
